import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { ArrowLeft, Eye, EyeOff } from "lucide-react";
import * as Sentry from '@sentry/react';

const ResetPassword = () => {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isVerifyingLink, setIsVerifyingLink] = useState(true);
  const [linkError, setLinkError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const initFromLink = async () => {
      setIsVerifyingLink(true);
      setLinkError(null);

      try {
        const currentUrl = new URL(window.location.href);
        const hashParams = new URLSearchParams(currentUrl.hash.replace(/^#/, ""));

        // Supabase may redirect back with an error in the hash
        const error = hashParams.get("error") ?? currentUrl.searchParams.get("error");
        const errorDescription =
          hashParams.get("error_description") ?? currentUrl.searchParams.get("error_description");

        if (error) {
          const msg = decodeURIComponent(
            errorDescription ?? "Email link is invalid or has expired. Please request a new password reset."
          );
          setLinkError(msg);
          toast.error(msg);
          return;
        }

        // PKCE flow: redirect contains a one-time `code`
        const code = currentUrl.searchParams.get("code");
        if (code) {
          const { error: exchangeError } = await supabase.auth.exchangeCodeForSession(code);
          if (exchangeError) {
            Sentry.captureException(exchangeError, { extra: { action: "exchangeCodeForSession" } });
            const msg = "Invalid or expired reset link. Please request a new password reset.";
            setLinkError(msg);
            toast.error(msg);
            return;
          }

          // Remove the one-time code from the URL (refreshing would otherwise fail)
          window.history.replaceState({}, document.title, currentUrl.pathname);
          return;
        }

        // Implicit flow: tokens appear in the hash
        const accessToken = hashParams.get("access_token");
        const refreshToken = hashParams.get("refresh_token");

        if (accessToken && refreshToken) {
          const { error: setSessionError } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken,
          });

          if (setSessionError) {
            Sentry.captureException(setSessionError, { extra: { action: "setSessionFromHash" } });
            const msg = "Invalid or expired reset link. Please request a new password reset.";
            setLinkError(msg);
            toast.error(msg);
            return;
          }

          return;
        }

        // If detectSessionInUrl=true, Supabase might have already consumed the URL and created a session.
        const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
        if (sessionError) {
          Sentry.captureException(sessionError, { extra: { action: "getSession" } });
          const msg = "Invalid or expired reset link. Please request a new password reset.";
          setLinkError(msg);
          toast.error(msg);
          return;
        }

        if (!sessionData.session) {
          const msg = "Invalid or expired reset link. Please request a new password reset.";
          setLinkError(msg);
          toast.error(msg);
          return;
        }
      } catch (error) {
        Sentry.captureException(error);
        const msg = "Invalid or expired reset link. Please request a new password reset.";
        setLinkError(msg);
        toast.error(msg);
      } finally {
        setIsVerifyingLink(false);
      }
    };

    initFromLink();
  }, []);

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();

    if (isVerifyingLink) return;

    if (linkError) {
      toast.error("Invalid or expired reset link. Please request a new one.");
      return;
    }
    if (password !== confirmPassword) {
      toast.error("Passwords don't match");
      return;
    }

    if (password.length < 6) {
      toast.error("Password must be at least 6 characters long");
      return;
    }

    setIsLoading(true);

    const { error } = await supabase.auth.updateUser({
      password: password
    });

    if (error) {
      Sentry.captureException(error, {
        extra: {
          action: 'updateUserPassword'
        }
      });
      toast.error(error.message);
    } else {
      toast.success("Password updated successfully! You can now sign in.");
      navigate('/login');
    }

    setIsLoading(false);
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-glyco-purple-light p-4">
      <div className="w-full max-w-md text-center mb-8 animate-fade-in">
        <h1 className="text-3xl font-bold mb-2 text-glyco-purple">Tastermonial Lab</h1>
        <p className="text-slate-600 font-medium">Don't Guess, Just Test</p>
      </div>

      <Card className="w-full max-w-md animate-fade-in">
        <CardHeader className="space-y-1">
          <CardTitle className="text-2xl text-center">Set new password</CardTitle>
          <CardDescription className="text-center">
            {linkError ? linkError : isVerifyingLink ? "Verifying reset link..." : "Enter your new password below"}
          </CardDescription>
        </CardHeader>

        <form onSubmit={handleResetPassword}>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="password">New Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter new password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={6}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4 text-gray-500" />
                  ) : (
                    <Eye className="h-4 w-4 text-gray-500" />
                  )}
                </Button>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirm New Password</Label>
              <div className="relative">
                <Input
                  id="confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  placeholder="Confirm new password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  minLength={6}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? (
                    <EyeOff className="h-4 w-4 text-gray-500" />
                  ) : (
                    <Eye className="h-4 w-4 text-gray-500" />
                  )}
                </Button>
              </div>
            </div>
          </CardContent>
          <CardFooter className="flex flex-col space-y-4">
            <Button
              type="submit"
              className="w-full bg-glyco-purple hover:bg-glyco-purple-dark"
              disabled={isLoading || isVerifyingLink || !!linkError}
            >
              {isVerifyingLink ? "Verifying..." : isLoading ? "Updating..." : "Update password"}
            </Button>
            <Link
              to="/login"
              className="text-sm text-glyco-purple hover:underline flex items-center justify-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to sign in
            </Link>
          </CardFooter>
        </form>
      </Card>
    </div>
  );
};

export default ResetPassword;